import { Link } from "react-router-dom";
import {
  ArrowRight, DollarSign, Clock, ShieldCheck, Home as HomeIcon,
  Handshake, Users, Star, MapPin, CheckCircle2,
} from "lucide-react";
import DOMPurify from "dompurify";
import { useSEO, BUSINESS_JSONLD } from "@/hooks/useSEO";

const STATS = [
  { n: "150+", label: "Houses purchased" },
  { n: "$28M+", label: "Cash deployed" },
  { n: "20 days", label: "Average close" },
  { n: "4.9", label: "Seller rating" },
];

const SERVICES = [
  {
    icon: DollarSign,
    title: "Cash Offer in 24 Hours",
    body: "Send us your address. You get a fair, no-obligation written offer within one business day. Zero commissions, no repairs required.",
  },
  {
    icon: Clock,
    title: "Close on Your Timeline",
    body: "Need to close in a week? Two months? We close when you're ready. Same title company for every deal, wire on close day.",
  },
  {
    icon: ShieldCheck,
    title: "No Repairs, No Cleaning",
    body: "Leave the house exactly as it is. Trash, tenants, code violations, fire damage. We've bought them all. Take what you want, leave the rest.",
  },
  {
    icon: HomeIcon,
    title: "Any Situation Welcome",
    body: "Inherited property, tired landlord, pre-foreclosure, divorce, relocation. We've navigated every scenario with discretion and respect.",
  },
  {
    icon: Handshake,
    title: "Fair, Transparent Pricing",
    body: "Every offer is backed by real neighborhood comps we show you. No lowball bait-and-switch. If we can't hit your number, we say so up front.",
  },
  {
    icon: Users,
    title: "Local, Not a Call Center",
    body: "Real Southwest Florida operators. You'll talk to the same person from first call to closing. No 1-800 handoff, no offshore scripts.",
  },
];

const HOW_IT_WORKS = [
  { n: "01", title: "Tell us about the house",
    body: "Fill out a 60-second form or call us. Address, condition, timeline. That's it." },
  { n: "02", title: "We analyze &amp; comp it",
    body: "Our team + AI platform pulls sold comps, ARV, rehab estimate. No credit checks, no drive-bys required." },
  { n: "03", title: "Get your written offer",
    body: "Within 24 hours you receive a signed, no-obligation cash offer. Ask questions. We walk you through the math." },
  { n: "04", title: "Close &amp; get paid",
    body: "Pick your close date. Sign at a title company (or remote if preferred). Funds wire to you the same day." },
];

// Placeholder testimonials. 5 sellers who sold houses to FlipPilot.
// [REPLACE ME] once you have real reviews.
const TESTIMONIALS = [
  {
    quote:
      "Inherited my mother's house in Cape Coral. Full of stuff, roof leaking, way too much for me to deal with from out of state. FlipPilot came through with a fair cash offer in 18 hours and closed 9 days later. I didn't lift a finger.",
    name: "Denise R.",
    city: "Fort Myers, FL",
    tag: "Inherited property",
  },
  {
    quote:
      "Landlord life burned me out. My last tenant left the place trashed. I couldn't afford another 3 months of vacancy. Sold to FlipPilot as-is, walked away with a check, done. Best decision I made all year.",
    name: "Marcus T.",
    city: "Lehigh Acres, FL",
    tag: "Tired landlord",
  },
  {
    quote:
      "I was 45 days from foreclosure and terrified. FlipPilot was the only buyer who actually explained the whole process without pressure. Closed in 11 days and I kept enough to move my family into an apartment with breathing room.",
    name: "Karen S.",
    city: "Naples, FL",
    tag: "Pre-foreclosure",
  },
  {
    quote:
      "Divorce forced a sale. My ex and I couldn't agree on a realtor, and every retail buyer wanted repairs we didn't have time or money for. FlipPilot's offer was $12K higher than the local iBuyer and they didn't renegotiate at close. Straight up.",
    name: "Jorge P.",
    city: "Port Charlotte, FL",
    tag: "Divorce sale",
  },
  {
    quote:
      "Job relocated me to Texas with two weeks' notice. Listed with an agent first. 3 months, no offers, still paying the mortgage. Switched to FlipPilot on a Tuesday. Signed by Thursday, closed the next Friday. Wish I'd called them first.",
    name: "Aisha L.",
    city: "Bonita Springs, FL",
    tag: "Job relocation",
  },
];

const FAQ = [
  { q: "How is your cash offer calculated?",
    a: "We pull recently sold comps within roughly half a mile, estimate any repair work needed to resell at retail, then work backwards from the after-repair value to arrive at a number that lets you walk away with cash today. We're happy to walk through the math with you line-by-line." },
  { q: "How fast can you actually close?",
    a: "As fast as 14 days once you accept the offer, though most sellers pick 20–30 days to give themselves time to move. We adjust to your timeline. Never the other way around." },
  { q: "Are there any fees or commissions?",
    a: "None. No agent commissions (3-6% saved), no closing costs on your side, no repair credits. The number on the offer is what you receive at closing." },
  { q: "Do I need to clean out the house?",
    a: "No. Take whatever you want and leave the rest. We handle disposal. This includes garbage, old appliances, tenant belongings, everything." },
  { q: "What if the house needs major repairs?",
    a: "That's our specialty. Roof damage, foundation issues, fire, mold, code violations. We buy houses in any condition. Never had a house scare us off." },
  { q: "How is this different from Opendoor or Offerpad?",
    a: "iBuyers use algorithms with hidden fees (usually 5-8% deducted at close) and often renegotiate after inspection. We give you one number, we don't charge fees, and we don't renegotiate. Also. We're local, so you talk to a human." },
];

export default function Home() {
  useSEO({
    title: "FlipPilot. Sell Your House Fast for Cash in Southwest Florida",
    description:
      "Get a fair, no-obligation cash offer within 24 hours. No repairs, no commissions, close in as little as 14 days. Serving Cape Coral, Fort Myers, Naples, and all of Southwest Florida.",
    path: "/",
    keywords:
      "sell house fast Florida, cash for houses Cape Coral, sell my house Fort Myers, cash home buyer Naples, we buy houses SWFL, sell inherited property Florida",
    jsonLd: BUSINESS_JSONLD,
  });

  return (
    <>
      {/* Hero */}
      <section className="mkt-grain pt-10 md:pt-16 pb-24" data-testid="hero-section">
        <div className="mkt-container">
          <div className="mkt-reveal">
            <span className="mkt-chip">
              <MapPin size={12} strokeWidth={2.5} />
              Serving Southwest Florida
            </span>
          </div>
          <h1
            className="mkt-hero-title mt-6 max-w-4xl mkt-reveal"
            style={{ "--reveal-delay": "0.1s" }}
            data-testid="hero-title"
          >
            Sell your house <span style={{ color: "var(--mkt-accent)" }}>fast</span>.
            <br /> Get a cash offer this week.
          </h1>
          <p
            className="mt-6 text-lg md:text-xl text-[var(--mkt-text-muted)] max-w-2xl mkt-reveal"
            style={{ "--reveal-delay": "0.2s" }}
          >
            No repairs, no commissions, no showings. Enter your address and we&rsquo;ll
            send a fair written offer within 24 hours. Close in as little as 14 days.
          </p>
          <div
            className="mt-8 flex flex-wrap gap-3 mkt-reveal"
            style={{ "--reveal-delay": "0.3s" }}
          >
            <Link to="/contact" className="mkt-btn mkt-btn-primary" data-testid="hero-cta-primary">
              Get My Cash Offer
              <ArrowRight size={16} />
            </Link>
            <Link to="/services" className="mkt-btn mkt-btn-ghost" data-testid="hero-cta-secondary">
              How it works
            </Link>
          </div>

          {/* Stats bar */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4">
            {STATS.map((s, i) => (
              <div
                key={s.label}
                className="mkt-card p-5 mkt-reveal"
                style={{ "--reveal-delay": `${0.4 + i * 0.08}s` }}
                data-testid={`stat-${i}`}
              >
                <div className="text-3xl md:text-4xl mkt-display" style={{ color: "var(--mkt-accent)" }}>
                  {s.n}
                </div>
                <div className="text-xs uppercase tracking-widest text-[var(--mkt-text-muted)] mt-2">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 bg-[var(--mkt-surface)]" data-testid="services-section">
        <div className="mkt-container">
          <span className="mkt-eyebrow">/ Why sellers choose us</span>
          <h2 className="mkt-section-title mt-3 max-w-3xl">
            The stress-free way to sell your house.
          </h2>
          <p className="mt-4 text-[var(--mkt-text-muted)] max-w-2xl">
            Real estate the traditional way takes months, costs thousands, and
            leaves you at the mercy of contingencies. We designed this for people
            who need out. Cleanly, quickly, on their terms.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
            {SERVICES.map(({ icon: Icon, title, body }, i) => (
              <div
                key={title}
                className="mkt-card p-6 mkt-reveal"
                style={{ "--reveal-delay": `${0.05 + i * 0.05}s` }}
                data-testid={`service-${i}`}
              >
                <div
                  className="w-11 h-11 grid place-items-center rounded-full"
                  style={{ background: "var(--mkt-surface-2)", color: "var(--mkt-accent-dark)" }}
                >
                  <Icon size={18} strokeWidth={2.2} />
                </div>
                <h3 className="text-lg font-bold mt-4">{title}</h3>
                <p className="text-sm text-[var(--mkt-text-muted)] mt-2 leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24" data-testid="how-it-works-section">
        <div className="mkt-container">
          <span className="mkt-eyebrow">/ How it works</span>
          <h2 className="mkt-section-title mt-3 max-w-3xl">
            Four steps. About a week from call to close.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
            {HOW_IT_WORKS.map((s, i) => (
              <div key={s.n} className="mkt-card p-6" data-testid={`step-${i}`}>
                <div
                  className="text-4xl mkt-display"
                  style={{ color: "var(--mkt-accent)" }}
                >
                  {s.n}
                </div>
                <h3 className="text-lg font-bold mt-4" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(s.title) }} />
                <p className="text-sm text-[var(--mkt-text-muted)] mt-2 leading-relaxed">
                  {s.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-[var(--mkt-surface)]" data-testid="testimonials-section">
        <div className="mkt-container">
          <span className="mkt-eyebrow">/ Reviews from real sellers</span>
          <h2 className="mkt-section-title mt-3 max-w-3xl">
            Trusted by Southwest Florida homeowners.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
            {TESTIMONIALS.map((t, i) => (
              <article
                key={i}
                className="mkt-card p-6"
                data-testid={`testimonial-${i}`}
              >
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} size={14} fill="var(--mkt-accent)" stroke="var(--mkt-accent)" />
                  ))}
                </div>
                <p className="text-sm leading-relaxed text-[var(--mkt-text)]">
                  &ldquo;{t.quote}&rdquo;
                </p>
                <div className="mt-5 pt-4 border-t border-[var(--mkt-line)] flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-sm">{t.name}</div>
                    <div className="text-xs text-[var(--mkt-text-muted)]">{t.city}</div>
                  </div>
                  <span className="mkt-chip">{t.tag}</span>
                </div>
              </article>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/reviews" className="mkt-link text-sm">
              Read all reviews →
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24" data-testid="faq-section">
        <div className="mkt-container grid md:grid-cols-3 gap-10">
          <div>
            <span className="mkt-eyebrow">/ FAQ</span>
            <h2 className="mkt-section-title mt-3">
              Straight answers, no fine print.
            </h2>
            <p className="mt-4 text-[var(--mkt-text-muted)]">
              Still curious? <Link to="/contact" className="mkt-link">Send a question</Link> and
              we&rsquo;ll respond within business hours. Usually much faster.
            </p>
          </div>
          <div className="md:col-span-2">
            {FAQ.map((f, i) => (
              <details
                key={i}
                className="group border-b border-[var(--mkt-line)] py-5"
                data-testid={`faq-${i}`}
              >
                <summary className="cursor-pointer list-none flex items-start justify-between gap-4">
                  <span className="font-semibold pr-4">{f.q}</span>
                  <span className="text-2xl leading-none text-[var(--mkt-accent)] group-open:rotate-45 transition-transform">
                    +
                  </span>
                </summary>
                <p className="text-sm text-[var(--mkt-text-muted)] mt-3 leading-relaxed">
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20" data-testid="final-cta">
        <div className="mkt-container">
          <div className="mkt-card-dark px-8 py-14 md:px-16 md:py-20 text-center">
            <span className="mkt-eyebrow" style={{ color: "var(--mkt-accent-2)" }}>
              / Ready when you are
            </span>
            <h2 className="mkt-section-title mt-4 text-white">
              Get a fair cash offer <br />
              <span style={{ color: "var(--mkt-accent-2)" }}>in under 24 hours.</span>
            </h2>
            <p className="mt-4 max-w-xl mx-auto text-white/70">
              No obligation, no fees, no pressure. If our number doesn&rsquo;t work
              for you, we shake hands and part ways.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Link to="/contact" className="mkt-btn mkt-btn-accent">
                Start My Offer
                <ArrowRight size={16} />
              </Link>
              <a href="tel:+12394808400" className="mkt-btn mkt-btn-ghost-dark">
                <CheckCircle2 size={16} />
                Call (239) 480-8400
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
